import { manufacturingFetch } from "@/lib/manufacturingClient";
import type { ColorCrosswalkField, ColorCrosswalkModifier } from "@/lib/manufacturing/colorCrosswalk";

export type InventoryMatchIndicator = {
  field: string;
  op: "equals" | "contains";
  value: string;
};

export type InventoryMatchRule = {
  id: string;
  material_use_id: string;
  use_code: string;
  material_id: string;
  material_code: string;
  material_name: string;
  priority: number;
  is_active: boolean;
  indicators: InventoryMatchIndicator[];
};

export type InventoryOutlookSettings = {
  email_enabled: boolean;
  cadence: "weekly" | "biweekly" | "monthly";
  email_from: string;
  last_sent_on: string | null;
  opted_in: boolean;
};

export type OutlookCadenceRecipient = {
  user_id: string;
  name: string;
  email: string | null;
  opted_in: boolean;
};

export type InventoryOutlookSettingsResponse = {
  ok: true;
  settings: InventoryOutlookSettings;
  can_manage_recipients: boolean;
  recipients: OutlookCadenceRecipient[];
};

export type InventoryOutlookReport = {
  unit_count: number;
  plant_id?: string | null;
  horizons: Array<{
    days: 7 | 14 | 30;
    rows: Array<{
      material_id: string;
      material_code: string;
      material_name: string;
      need: number;
      on_hand: number;
      short: number;
    }>;
    unresolved: Array<{
      material_use_id: string;
      use_code: string;
      quantity: number;
      reason: "no_match" | "ambiguous";
    }>;
  }>;
  supply?: Array<{
    material_id: string;
    material_code: string;
    material_name: string;
    short: number;
    on_hand: number;
    draft_qty: number;
    submitted_qty: number;
    arrivals: Array<{ date: string | null; qty: number }>;
    uncovered: number;
  }>;
  serials?: Array<{
    unit_id: string;
    serial: string;
    plant_id: string;
    material_id: string;
    material_code: string;
    material_name: string;
    quantity: number;
    uncovered_qty: number;
    po_numbers: string[];
  }>;
  gaps?: Array<{
    unit_id: string;
    serial: string;
    plant_id: string;
    material_use_id: string;
    use_code: string;
    material_id: string;
    material_code: string;
    material_name: string;
    quantity: number;
    reason: "assumed" | "no_match" | "ambiguous";
    finish_channel?: string | null;
    aspects: Array<{ field: string; value: string }>;
  }>;
  finish_options?: Array<{
    material_use_id: string;
    use_code: string;
    finish_channel: string | null;
    finish_codes: string[];
    members?: Array<{
      member_id: string;
      material_id: string;
      material_code: string;
      material_name: string;
      finish_code: string | null;
    }>;
  }>;
};

export type ColorCrosswalkRecord = {
  id: string;
  source_field: ColorCrosswalkField;
  source_value: string;
  modifiers: ColorCrosswalkModifier[];
  finish_channel: string;
  finish_code: string;
  priority: number;
  is_active: boolean;
};

export function listColorCrosswalk() {
  return manufacturingFetch<{ ok: true; rows: ColorCrosswalkRecord[] }>("/api/mfg/inventory-color-crosswalk", {
    method: "GET",
  });
}

export function upsertColorCrosswalk(payload: {
  id?: string;
  source_field: ColorCrosswalkField;
  source_value: string;
  modifiers: ColorCrosswalkModifier[];
  finish_channel: string;
  finish_code: string;
  priority?: number;
}) {
  return manufacturingFetch<{ ok: true; row: ColorCrosswalkRecord }>("/api/mfg/inventory-color-crosswalk/upsert", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function deleteColorCrosswalk(id: string) {
  return manufacturingFetch<{ ok: true }>("/api/mfg/inventory-color-crosswalk/delete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id }),
  });
}

export function listInventoryMatchRules() {
  return manufacturingFetch<{ ok: true; rules: InventoryMatchRule[] }>("/api/mfg/inventory-match-rules", {
    method: "GET",
  });
}

export function upsertInventoryMatchRule(payload: {
  id?: string;
  material_use_id: string;
  material_id: string;
  priority: number;
  indicators: InventoryMatchIndicator[];
}) {
  return manufacturingFetch<{ ok: true; rule: InventoryMatchRule }>("/api/mfg/inventory-match-rules/upsert", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function deleteInventoryMatchRule(id: string) {
  return manufacturingFetch<{ ok: true }>("/api/mfg/inventory-match-rules/delete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id }),
  });
}

export function emailCurrentInventoryOutlook(report: InventoryOutlookReport) {
  return manufacturingFetch<{ ok: true; sent_to: string }>("/api/mfg/inventory-outlook/email-me", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ report: { unit_count: report.unit_count, horizons: report.horizons } }),
  });
}

export function getInventoryOutlook() {
  return manufacturingFetch<{ ok: true; report: InventoryOutlookReport }>("/api/mfg/inventory-outlook", {
    method: "GET",
  });
}

export function getInventoryOutlookSettings() {
  return manufacturingFetch<InventoryOutlookSettingsResponse>("/api/mfg/inventory-outlook/settings", {
    method: "GET",
  });
}

export function saveInventoryOutlookSettings(payload: Partial<InventoryOutlookSettings>) {
  return manufacturingFetch<InventoryOutlookSettingsResponse>("/api/mfg/inventory-outlook/settings", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function setOutlookCadenceRecipient(userId: string, optedIn: boolean) {
  return manufacturingFetch<InventoryOutlookSettingsResponse>("/api/mfg/inventory-outlook/settings", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ recipient_user_id: userId, opted_in: optedIn }),
  });
}
